import { Component, OnDestroy, OnInit } from '@angular/core';
import { PaymentType, WebkassaCashboxModel, WebkassaCashboxStatus } from 'hx-services';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { BehaviorSubject, Subject } from 'rxjs';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { WebkassaCashboxService } from '@adm-web-app/services/webkassa-cashbox.service';
import { debounceTime, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { NzAffixComponent } from 'ng-zorro-antd/affix';
import { HxBreadcrumbsComponent } from '@adm-web-app/components/breadcrumbs/breadcrumbs.component';
import { NzPageHeaderComponent, NzPageHeaderTitleDirective } from 'ng-zorro-antd/page-header';
import { NzSkeletonComponent } from 'ng-zorro-antd/skeleton';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzRadioComponent, NzRadioGroupComponent } from 'ng-zorro-antd/radio';
import { HxEnumSelectComponent, HxOrgSelectComponent, HxStoreSelectComponent } from 'hx-components';
import { NzOptionComponent, NzSelectComponent } from 'ng-zorro-antd/select';
import { NgFor, NgIf } from '@angular/common';
import { NzIconDirective } from 'ng-zorro-antd/icon';

import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';
import { NzTagComponent } from 'ng-zorro-antd/tag';

@Component({
    selector: 'hc-webkassa-cashbox-edit',
    templateUrl: './webkassa-cashbox-edit.component.html',
    styleUrls: ['./webkassa-cashbox-edit.component.less'],
  imports: [TranslocoDirective, NzAffixComponent, HxBreadcrumbsComponent, NzPageHeaderComponent, NzPageHeaderTitleDirective, NzSkeletonComponent, FormsModule, NzFormDirective, ReactiveFormsModule, NzRowDirective, NzColDirective, NzFormItemComponent, NzFormLabelComponent, NzFormControlComponent, NzInputDirective, NzRadioGroupComponent, NzRadioComponent, HxOrgSelectComponent, HxStoreSelectComponent, HxEnumSelectComponent, NzSelectComponent, NgIf, NgFor, NzOptionComponent, NzIconDirective, NzAlertComponent, NzButtonComponent, NzWaveDirective, NzTagComponent, TranslocoPipe]
})
export class WebkassaCashboxEditComponent implements OnInit, OnDestroy {
  model?: WebkassaCashboxModel;
  isEdit = false;
  isCreate = false;
  form = new FormGroup({
    enabled: new FormControl<boolean | null>(null, [Validators.required]),
    uniqueNumber: new FormControl<string | null>(null, [Validators.required]),
    registrationNumber: new FormControl<string | null>(null, [Validators.required]),
    identificationNumber: new FormControl<string | null>(null, [Validators.required]),
    name: new FormControl<string | null>(null, [Validators.required]),
    description: new FormControl<string | null>(null),
    offline: new FormControl<boolean | null>(null, [Validators.required]),
    status: new FormControl<WebkassaCashboxStatus | null>(null, [Validators.required]),
    organizationId: new FormControl<number | null>(null, [Validators.required]),
    storeId: new FormControl<number | null>(null),
    onlineCashboxId: new FormControl<number | null>(null),
  });
  isSaving = false;
  isLoading = {
    cashboxes: false,
    model: false,
  };
  cashboxes: WebkassaCashboxModel[] = [];
  showSearchHint = false;
  private $destroyed = new Subject<void>();
  private searchChange$ = new BehaviorSubject('');

  constructor(
    private aRoute: ActivatedRoute,
    private webkassaCashboxService: WebkassaCashboxService,
    private tr: TranslocoService,
    private notification: NzNotificationService,
    private router: Router,
  ) {
  }

  async ngOnInit() {
    const routeData: Data = this.aRoute.snapshot.data;
    this.model = routeData['model'] as WebkassaCashboxModel;
    if (this.model) {
      this.isEdit = true;
    } else {
      this.isCreate = true;
    }

    if (this.isEdit) {
      if (this.model.onlineCashboxId) {
        const cashbox = await this.webkassaCashboxService.getCashboxById(this.model.onlineCashboxId);
        this.cashboxes = [cashbox];
      }
    }

    this.form.patchValue({
      enabled: this.model?.enabled,
      uniqueNumber: this.model?.uniqueNumber,
      registrationNumber: this.model?.registrationNumber,
      identificationNumber: this.model?.identificationNumber,
      name: this.model?.name,
      description: this.model?.description,
      offline: this.model?.offline,
      status: this.model?.status as WebkassaCashboxStatus,
      organizationId: this.model?.organization.id,
      storeId: this.model?.store?.id,
      onlineCashboxId: this.model?.onlineCashboxId,
    });

    this.searchChange$
      .pipe(
        takeUntil(this.$destroyed),
        debounceTime(500),
        filter(q => (q ?? '').trim().length > 0),
        switchMap(name => this.webkassaCashboxService.getCashboxList({query: name, includeDisabled: false})),
        map(res => res.list)
      )
      .subscribe(cashboxes => {
        this.cashboxes = cashboxes;
        this.isLoading.cashboxes = false;
      });
  }

  async submitForm() {
    Object.values(this.form.controls).forEach(ctrl => {
      ctrl.markAsDirty();
      ctrl.updateValueAndValidity();
    });
    if (this.form.valid) {
      const formModel = this.form.value;

      this.isSaving = true;
      try {
        if (formModel.enabled === null
          || formModel.enabled === undefined
          || !formModel.uniqueNumber
          || !formModel.registrationNumber
          || !formModel.identificationNumber
          || !formModel.name
          || !formModel.status
          || !formModel.organizationId
          || formModel.offline === null
          || formModel.offline === undefined
        ) {
          throw new Error('form.required.fields');
        }
        const {id} = await this.webkassaCashboxService.saveCashbox({
          id: this.model?.id,
          enabled: formModel.enabled,
          uniqueNumber: formModel.uniqueNumber,
          registrationNumber: formModel.registrationNumber,
          identificationNumber: formModel.identificationNumber,
          name: formModel.name,
          description: formModel.description ?? undefined,
          offline: formModel.offline,
          status: formModel.status,
          organizationId: formModel.organizationId,
          storeId: formModel.storeId ?? undefined,
          onlineCashboxId: formModel.onlineCashboxId ?? undefined,
        });

        this.form.markAsPristine();
        this.notification.create('success', 'Webkassa', this.tr.translate('nt.dataSaved'));
        if (this.isCreate) {
          await this.router.navigate([`/webkassa-cashboxes/${id}/edit`]);
        } else {
          this.isLoading.model = true;
          this.model = await this.webkassaCashboxService.getCashboxById(id);
        }
      } finally {
        this.isSaving = false;
        this.isLoading.model = false;
      }
    } else {
      console.log('not valid form');
    }
  }

  ngOnDestroy(): void {
    this.$destroyed.next();
    this.$destroyed.complete();
  }

  onOnlineCashboxSearch(value: string) {
    this.showSearchHint = value === null || value === undefined || value.trim() === '';
    this.isLoading.cashboxes = true;
    this.searchChange$.next(value);
  }

  protected readonly PaymentType = PaymentType;
  protected readonly WebkassaCashboxStatus = WebkassaCashboxStatus;
}
